import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import '../App.css';

interface DbSnapshot {
  id: number;
  name: string;
  kind: string;
  createdAt: string;
  createdByEmail?: string | null;
  sizeBytes: number;
  tableCount: number;
  rowCount: number;
  notes?: string | null;
}

function formatBytes(n: number) {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatWhen(iso: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );
  useEffect(() => {
    const apply = () => setIsNarrow(window.innerWidth <= 767);
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);
  return isNarrow;
}

/** SuperAdmin: in-DB logical snapshots for testing restore (Postgres only). */
const DbSnapshots: React.FC = () => {
  const isNarrow = useIsNarrow();
  const [snapshots, setSnapshots] = useState<DbSnapshot[]>([]);
  const [maxSnapshots, setMaxSnapshots] = useState(5);
  const [restoreConfirm, setRestoreConfirm] = useState('RESTORE_SNAPSHOT');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await apiFetch('/api/admin/snapshots');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `HTTP ${res.status}`);
    }
    setSnapshots(Array.isArray(data.snapshots) ? data.snapshots : []);
    if (data.maxSnapshots) setMaxSnapshots(Number(data.maxSnapshots));
    if (data.restoreConfirm) setRestoreConfirm(String(data.restoreConfirm));
  }, []);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
  }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setSuccess(null);
    const label = name.trim();
    if (!label) {
      setError('Name is required');
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch('/api/admin/snapshots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: label }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setName('');
      setSnapshots(Array.isArray(data.snapshots) ? data.snapshots : []);
      setSuccess(`Created snapshot “${data.snapshot?.name || label}”`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const restore = async (snap: DbSnapshot) => {
    if (busy) return;
    const typed = window.prompt(
      `Restore snapshot “${snap.name}”? This replaces app data.\n` +
        `An automatic pre-restore snapshot is taken first.\n` +
        `Type ${restoreConfirm} to confirm:`
    );
    if (typed == null) return;
    if (typed !== restoreConfirm) {
      setError(`Confirmation must be exactly ${restoreConfirm}`);
      return;
    }
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const res = await apiFetch(`/api/admin/snapshots/${snap.id}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Confirm-Token': restoreConfirm },
        body: JSON.stringify({ confirm: restoreConfirm }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSnapshots(Array.isArray(data.snapshots) ? data.snapshots : []);
      setSuccess(
        `Restored “${snap.name}”. Pre-restore snapshot #${data.preRestoreSnapshot?.id || '?'} saved.`
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (snap: DbSnapshot) => {
    if (busy) return;
    if (!window.confirm(`Delete snapshot “${snap.name}”?`)) return;
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const res = await apiFetch(`/api/admin/snapshots/${snap.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSnapshots(Array.isArray(data.snapshots) ? data.snapshots : []);
      setSuccess(`Deleted “${snap.name}”`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="page-container container flow-page snapshots-page"
      data-testid="db-snapshots-page"
      data-narrow={isNarrow ? 'true' : 'false'}
    >
      <h2 className="app-header">DB Snapshots</h2>
      <p className="flow-hint">
        SuperAdmin testing tool: store a full logical copy of app tables inside Postgres (no shell).
        Max {maxSnapshots} snapshots (oldest pruned). Restore takes an automatic pre-restore
        snapshot and keeps your admin login.
      </p>
      {error && <div className="error">{error}</div>}
      {success && (
        <div className="error" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
          {success}
        </div>
      )}

      <form onSubmit={create} className="flow-card" style={{ marginBottom: 16 }}>
        <div className="flow-field">
          <label htmlFor="snapshot-name">Snapshot name</label>
          <input
            id="snapshot-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. before ekos dry-run"
            data-testid="snapshot-name"
            disabled={busy}
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="snapshot-create"
          className="btn-primary"
          style={{ width: '100%', minHeight: 48, background: '#2196F3', color: '#fff' }}
        >
          {busy ? 'Working…' : 'Create snapshot'}
        </button>
      </form>

      <div className="flow-mobile-cards flow-card-list" data-testid="snapshots-cards">
        {snapshots.map((s) => (
          <div key={s.id} className="flow-card" data-testid={`snapshot-card-${s.id}`}>
            <div className="flow-card-row">
              <p className="flow-card-title" style={{ margin: 0 }}>
                {s.name}
              </p>
              <span className="flow-badge">{s.kind}</span>
            </div>
            <p className="flow-card-meta">{formatWhen(s.createdAt)}</p>
            <p className="flow-card-meta">
              {formatBytes(s.sizeBytes)} · {s.tableCount} tables · {s.rowCount} rows
            </p>
            <p className="flow-card-meta">By {s.createdByEmail || '—'}</p>
            <div className="flow-card-actions">
              <button
                type="button"
                data-testid={`snapshot-restore-${s.id}`}
                disabled={busy}
                onClick={() => restore(s)}
              >
                Restore
              </button>
              <button
                type="button"
                data-testid={`snapshot-delete-${s.id}`}
                disabled={busy}
                onClick={() => remove(s)}
                style={{ background: '#ffe6e6', color: '#a00' }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {!snapshots.length && <p className="flow-hint">No snapshots yet.</p>}
      </div>

      <table className="inventory-table flow-desktop-table" data-testid="snapshots-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Kind</th>
            <th>Created</th>
            <th>Size</th>
            <th>Tables</th>
            <th>Rows</th>
            <th>By</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {snapshots.map((s) => (
            <tr key={s.id} data-testid={`snapshot-row-${s.id}`}>
              <td>{s.name}</td>
              <td>{s.kind}</td>
              <td>{formatWhen(s.createdAt)}</td>
              <td>{formatBytes(s.sizeBytes)}</td>
              <td>{s.tableCount}</td>
              <td>{s.rowCount}</td>
              <td>{s.createdByEmail || '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button
                  type="button"
                  data-testid={`snapshot-restore-desk-${s.id}`}
                  disabled={busy}
                  onClick={() => restore(s)}
                  style={{ marginRight: 8, minHeight: 44 }}
                >
                  Restore
                </button>
                <button
                  type="button"
                  data-testid={`snapshot-delete-desk-${s.id}`}
                  disabled={busy}
                  onClick={() => remove(s)}
                  style={{ color: '#a00', minHeight: 44 }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {!snapshots.length && (
            <tr>
              <td colSpan={8} style={{ color: '#777' }}>
                No snapshots yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DbSnapshots;
